import { trackAction } from '@cint/analytics';
import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTriggerType,
  DateInput,
  MenuItems,
  Spinner,
} from '@cint/wonderment';
import { useCallback, useEffect, useMemo } from 'react';

import { EVENTS } from '@/analytics';

export type FiltersType = {
  selectedDates: {
    from: Date;
    to: Date;
  };
  selectedInterval: 'weekly' | 'daily' | 'monthly';
};

interface ReportDateFiltersProps {
  filters: FiltersType;
  onFiltersChange: (val: any) => void;
  minDate?: Date | null;
  hasMinDate?: boolean;
}

const getDateRangeForPicker = () => {
  const now = new Date();
  const toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

  const oneMonthBefore = new Date(
    toDate.getFullYear(),
    toDate.getMonth(),
    toDate.getDate() - 30
  );

  return {
    yesterday: toDate,
    oneMonthBeforeYesterday: oneMonthBefore,
  };
};

const timeIntervalOptions = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

export const ReportDateFilters = ({
  filters,
  onFiltersChange,
  hasMinDate = false,
  minDate = null,
}: ReportDateFiltersProps) => {
  const dateRangeForPicker = getDateRangeForPicker();

  const effectiveMinDate = useMemo(() => {
    return minDate || null;
  }, [minDate]);

  const adjustFiltersIfNeeded = useCallback(() => {
    if (
      filters?.selectedDates &&
      hasMinDate &&
      effectiveMinDate &&
      filters.selectedDates.from < effectiveMinDate
    ) {
      onFiltersChange({
        selectedDates: {
          from: effectiveMinDate,
          to: filters.selectedDates.to || effectiveMinDate,
        },
      });
    }
  }, [filters, hasMinDate, effectiveMinDate, onFiltersChange]);

  useEffect(() => {
    adjustFiltersIfNeeded();
  }, [adjustFiltersIfNeeded]);

  const handleSelectInterval = (value: 'daily' | 'weekly' | 'monthly') => {
    if (value === 'daily') {
      trackAction(EVENTS.DATE_FILTER_DAILY);
    } else if (value === 'weekly') {
      trackAction(EVENTS.DATE_FILTER_WEEKLY);
    } else if (value === 'monthly') {
      trackAction(EVENTS.DATE_FILTER_MONTHLY);
    }
    onFiltersChange({ selectedInterval: value });
  };

  const selectedDates = filters?.selectedDates
    ? [filters.selectedDates.from, filters.selectedDates.to]
    : [];

  return (
    <div>
      {!filters?.selectedDates?.from && <Spinner />}
      {filters?.selectedDates?.from && (
        <div className="flex gap-2">
          <div>
            <DateInput
              calendarProps={{
                mode: 'range',
                disabled: {
                  before: effectiveMinDate,
                  after: dateRangeForPicker.yesterday,
                },
              }}
              onDateSelected={([from, to]) => {
                if (from) {
                  onFiltersChange({
                    selectedDates: {
                      from: from,
                      to: to || from,
                    },
                  });
                }
              }}
              selected={selectedDates}
              hasClearButton={false}
              inputSize="base"
              placeholder="Select"
            />
          </div>
          <div>
            <ActionMenu
              triggerProps={{
                type: ActionMenuTriggerType.SelectButton,
                props: {
                  label: timeIntervalOptions.find(
                    ({ value }) => filters?.selectedInterval === value
                  )?.label,
                },
              }}
            >
              <ActionMenuContent>
                <MenuItems>
                  {timeIntervalOptions.map((option) => (
                    <ActionMenuItem
                      key={option.value}
                      onClick={() =>
                        handleSelectInterval(
                          option.value as 'daily' | 'weekly' | 'monthly'
                        )
                      }
                      label={option.label}
                    />
                  ))}
                </MenuItems>
              </ActionMenuContent>
            </ActionMenu>
          </div>
        </div>
      )}
    </div>
  );
};
